// contexts/CartContext.js
import React, { createContext, useContext, useReducer, useEffect } from 'react';

// Cart action types
const CART_ACTIONS = {
  ADD_ITEM: 'ADD_ITEM',
  REMOVE_ITEM: 'REMOVE_ITEM',
  UPDATE_QUANTITY: 'UPDATE_QUANTITY',
  CLEAR_CART: 'CLEAR_CART',
  LOAD_CART: 'LOAD_CART',
  SET_LOADING: 'SET_LOADING'
};

// Initial cart state
const initialCartState = {
  items: [],
  itemCount: 0,
  totalAmount: 0,
  loading: false
};

// Cart reducer
const cartReducer = (state, action) => {
  switch (action.type) {
    case CART_ACTIONS.LOAD_CART:
      return {
        ...state,
        ...action.payload,
        loading: false
      };

    case CART_ACTIONS.ADD_ITEM: {
      const { product, quantity = 1 } = action.payload;
      const existingItemIndex = state.items.findIndex(item => 
        item.id === product.id && item.type === product.type
      );

      let newItems;
      if (existingItemIndex >= 0) {
        // Update existing item quantity
        newItems = state.items.map((item, index) => 
          index === existingItemIndex 
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        // Add new item
        const cartItem = {
          id: product.id,
          type: product.type || 'product',
          title: product.title || product.name,
          price: product.salePrice || product.price,
          originalPrice: product.price,
          image: product.image || product.images?.[0] || product.primary_image,
          quantity: quantity,
          inStock: product.inStock !== false,
          stockQuantity: product.stockQuantity,
          category: product.category,
          isOnSale: product.isOnSale || false,
          addedAt: new Date().toISOString()
        };
        newItems = [...state.items, cartItem];
      }

      return calculateCartTotals({
        ...state,
        items: newItems
      });
    }

    case CART_ACTIONS.UPDATE_QUANTITY: {
      const { itemId, itemType, quantity } = action.payload;
      
      if (quantity <= 0) {
        return cartReducer(state, {
          type: CART_ACTIONS.REMOVE_ITEM,
          payload: { itemId, itemType }
        });
      }

      const newItems = state.items.map(item =>
        item.id === itemId && item.type === itemType
          ? { ...item, quantity: Math.max(1, quantity) }
          : item
      );

      return calculateCartTotals({
        ...state,
        items: newItems
      });
    }

    case CART_ACTIONS.REMOVE_ITEM: {
      const { itemId, itemType } = action.payload;
      const newItems = state.items.filter(item => 
        !(item.id === itemId && item.type === itemType)
      );

      return calculateCartTotals({
        ...state,
        items: newItems
      });
    }

    case CART_ACTIONS.CLEAR_CART:
      return {
        ...initialCartState,
        loading: false
      };

    case CART_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };

    default:
      return state;
  }
};

// Helper function to calculate cart totals
const calculateCartTotals = (state) => {
  const itemCount = state.items.reduce((total, item) => total + item.quantity, 0);
  const totalAmount = state.items.reduce((total, item) => total + (item.price * item.quantity), 0);

  return {
    ...state,
    itemCount,
    totalAmount: Number(totalAmount.toFixed(2))
  };
};

// Create context
const CartContext = createContext();

// Cart provider component
export const CartProvider = ({ children }) => {
  const [cart, dispatch] = useReducer(cartReducer, initialCartState);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('security_cart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        dispatch({
          type: CART_ACTIONS.LOAD_CART,
          payload: parsedCart
        });
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
        dispatch({ type: CART_ACTIONS.CLEAR_CART });
      }
    } else {
      dispatch({ type: CART_ACTIONS.SET_LOADING, payload: false });
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (!cart.loading) {
      localStorage.setItem('security_cart', JSON.stringify({
        items: cart.items,
        itemCount: cart.itemCount,
        totalAmount: cart.totalAmount
      }));
    }
  }, [cart]);

  // Cart actions
  const addToCart = (product, quantity = 1) => {
    if (!product.inStock && product.type === 'product') {
      throw new Error('Product is out of stock');
    }

    dispatch({
      type: CART_ACTIONS.ADD_ITEM,
      payload: { product, quantity }
    });

    return true;
  };

  const removeFromCart = (itemId, itemType = 'product') => {
    dispatch({
      type: CART_ACTIONS.REMOVE_ITEM,
      payload: { itemId, itemType }
    });
  };

  const updateQuantity = (itemId, itemType = 'product', quantity) => {
    dispatch({
      type: CART_ACTIONS.UPDATE_QUANTITY,
      payload: { itemId, itemType, quantity }
    });
  };

  const clearCart = () => {
    dispatch({ type: CART_ACTIONS.CLEAR_CART });
    localStorage.removeItem('security_cart');
  };

  const getItemQuantity = (itemId, itemType = 'product') => {
    const item = cart.items.find(item => 
      item.id === itemId && item.type === itemType
    );
    return item ? item.quantity : 0;
  };

  const isInCart = (itemId, itemType = 'product') => {
    return cart.items.some(item => 
      item.id === itemId && item.type === itemType
    );
  };

  const getCartSummary = () => {
    return {
      itemCount: cart.itemCount,
      totalAmount: cart.totalAmount,
      isEmpty: cart.items.length === 0,
      hasProducts: cart.items.some(item => item.type === 'product'),
      hasServices: cart.items.some(item => item.type === 'service')
    };
  };

  // Format price helper
  const formatPrice = (price) => {
    return `$${Number(price).toFixed(2)}`;
  };

  const contextValue = {
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getItemQuantity,
    isInCart,
    getCartSummary,
    formatPrice,
    loading: cart.loading
  };

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};

// Custom hook to use cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;